import type {
  Popover as PopoverPrimitive,
  Tooltip as TooltipPrimitive,
} from 'radix-ui';
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './popover.js';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './tooltip.js';

const TouchContext = createContext<boolean | undefined>(undefined);
const useTouch = () => useContext(TouchContext);

const TouchProvider = (props: PropsWithChildren) => {
  const [isTouch, setTouch] = useState<boolean>();

  useEffect(() => {
    setTouch(window.matchMedia('(pointer: coarse)').matches);
  }, []);

  return <TouchContext.Provider value={isTouch} {...props} />;
};

const HybridTooltipProvider = (
  props: React.ComponentProps<typeof TooltipPrimitive.Provider>,
) => {
  return <TooltipProvider delayDuration={0} {...props} />;
};

const HybridTooltip = (
  props: React.ComponentProps<typeof TooltipPrimitive.Root> &
    React.ComponentProps<typeof PopoverPrimitive.Root>,
) => {
  const isTouch = useTouch();

  return isTouch ? <Popover {...props} /> : <Tooltip {...props} />;
};

const HybridTooltipTrigger = (
  props: React.ComponentProps<typeof TooltipPrimitive.Trigger> &
    React.ComponentProps<typeof PopoverPrimitive.Trigger>,
) => {
  const isTouch = useTouch();

  return isTouch ? (
    <PopoverTrigger {...props} />
  ) : (
    <TooltipTrigger {...props} />
  );
};

const HybridTooltipContent = (
  props: React.ComponentProps<typeof TooltipPrimitive.Content> &
    React.ComponentProps<typeof PopoverPrimitive.Content>,
) => {
  const isTouch = useTouch();

  return isTouch ? (
    <PopoverContent {...props} />
  ) : (
    <TooltipContent {...props} />
  );
};

export {
  HybridTooltip,
  HybridTooltipContent,
  HybridTooltipProvider,
  HybridTooltipTrigger,
  TouchProvider,
};
